import type { SortDirection } from '@/types';

export const COACH_STATUSES = [
  'active',
  'offboarded',
  'training',
  'paused',
  'offboarding',
  'do_not_match',
  'fixed_capacity',
  'internal_employee',
] as const;

export type CoachStatus = (typeof COACH_STATUSES)[number];

export const COACH_STATUS_LABELS = {
  active: 'Active',
  do_not_match: 'DNM / Do Not Match',
  fixed_capacity: 'Fixed capacity',
  internal_employee: 'Internal Employee',
  offboarded: 'Non Active - Offboarded',
  offboarding: 'Offboarding',
  paused: 'Paused',
  training: 'In Training',
} satisfies Record<CoachStatus, string>;

export const COACH_STATUS_RANK = Object.fromEntries(
  COACH_STATUSES.map((status, index) => [status, index + 1]),
) as Record<CoachStatus, number>;

export const COACH_STATUS_BADGE_STYLES = {
  active: 'bg-green-50 text-green-700',
  do_not_match: 'bg-purple-50 text-purple-700',
  fixed_capacity: 'bg-teal-50 text-teal-700',
  internal_employee: 'bg-sage-100 text-sage-600',
  offboarded: 'bg-cement-100 text-cement-500',
  offboarding: 'bg-orange-50 text-orange-700',
  paused: 'bg-amber-50 text-amber-700',
  training: 'bg-blue-50 text-blue-700',
} satisfies Record<CoachStatus, string>;

export const COACH_STATUS_OPTIONS = COACH_STATUSES.map((status) => ({
  id: status,
  name: COACH_STATUS_LABELS[status],
}));

export const sortCoachStatuses = (statuses: readonly CoachStatus[]) =>
  [...statuses].sort((a, b) => COACH_STATUS_RANK[a] - COACH_STATUS_RANK[b]);

export const coachStatusesLabel = (statuses: readonly CoachStatus[]) =>
  statuses.length === 0
    ? 'Status missing'
    : sortCoachStatuses(statuses)
        .map((status) => COACH_STATUS_LABELS[status])
        .join(', ');

export const coachStatusDisplay = (status: CoachStatus | null) =>
  status === null
    ? {
        badgeClassName: 'bg-red-50 text-red-700',
        label: 'Status missing',
      }
    : {
        badgeClassName: COACH_STATUS_BADGE_STYLES[status],
        label: COACH_STATUS_LABELS[status],
      };

export const compareCoachStatuses = (
  left: CoachStatus | null,
  right: CoachStatus | null,
  direction: SortDirection,
  leftTieBreaker = '',
  rightTieBreaker = '',
) => {
  if (left === right) return leftTieBreaker.localeCompare(rightTieBreaker);
  if (left === null) return 1;
  if (right === null) return -1;

  const difference = COACH_STATUS_RANK[left] - COACH_STATUS_RANK[right];
  return direction === 'asc' ? difference : -difference;
};
